// Diagnostic error boundary for Recharts-heavy pages.
//
// When a page renders briefly then crashes (the classic Active Bug #6
// pattern from Session 65/66 — Market Pulse + Price Distribution
// errored out after data arrived on iOS but not on desktop), the
// app-level ErrorBoundary shows "Something went wrong" with no error
// message visible to the user. On mobile the user can't easily open
// a console to see the actual JS error.
//
// This boundary catches errors at the page level AND shows the actual
// error.message + truncated stack on screen, with a "Copy error" button
// so the user can paste the diagnostic into a chat/email/issue tracker.
//
// Wrap pages with it like:
//   <PageErrorBoundary pageName="Market Pulse">
//     <MarketPulse />
//   </PageErrorBoundary>

import { Component, ReactNode } from "react";
import { AlertTriangle, Copy, RotateCcw } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

interface Props {
  pageName: string;
  children: ReactNode;
}

interface State {
  hasError:   boolean;
  errorMsg:   string | null;
  errorStack: string | null;
  copyState:  "idle" | "copied";
}

export class PageErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, errorMsg: null, errorStack: null, copyState: "idle" };
  }

  static getDerivedStateFromError(error: Error): State {
    return {
      hasError:   true,
      errorMsg:   error?.message ?? String(error),
      errorStack: error?.stack ?? null,
      copyState:  "idle",
    };
  }

  componentDidCatch(error: Error, info: { componentStack: string }) {
    // Keep the console trail for desktop debugging
    console.error(`[PageErrorBoundary:${this.props.pageName}]`, error, info.componentStack);
  }

  handleCopy = () => {
    const payload = [
      `Page: ${this.props.pageName}`,
      `URL: ${typeof window !== "undefined" ? window.location.href : "(SSR)"}`,
      `Time: ${new Date().toISOString()}`,
      `UA: ${typeof navigator !== "undefined" ? navigator.userAgent : "(no nav)"}`,
      ``,
      `Error: ${this.state.errorMsg ?? "(no message)"}`,
      ``,
      `Stack (top 1500 chars):`,
      (this.state.errorStack ?? "(no stack)").slice(0, 1500),
    ].join("\n");
    try {
      navigator.clipboard?.writeText(payload);
      this.setState({ copyState: "copied" });
      setTimeout(() => this.setState({ copyState: "idle" }), 1500);
    } catch {
      // Older mobile browsers — fallback: select all text in a hidden textarea
      try {
        const ta = document.createElement("textarea");
        ta.value = payload;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand?.("copy");
        document.body.removeChild(ta);
        this.setState({ copyState: "copied" });
        setTimeout(() => this.setState({ copyState: "idle" }), 1500);
      } catch {
        /* give up silently */
      }
    }
  };

  handleReload = () => {
    try {
      window.location.reload();
    } catch {
      /* ignore */
    }
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <div className="min-h-[60vh] flex items-start justify-center p-4">
        <Card className="border-amber-500/40 bg-amber-500/5 max-w-2xl w-full">
          <CardHeader className="pb-2">
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              <CardTitle className="text-base font-semibold text-amber-600">
                {this.props.pageName} hit an unexpected error
              </CardTitle>
            </div>
          </CardHeader>
          <CardContent className="space-y-4 text-sm">
            <p className="text-muted-foreground">
              This page works on desktop and we're tracking a mobile-specific
              render bug. The exact error is shown below — please tap{" "}
              <strong>Copy error</strong> and send it back so we can target a
              fix.
            </p>
            <div className="rounded-md bg-muted/40 border border-border p-3 font-mono text-[12px] leading-relaxed break-words">
              <div className="font-semibold text-foreground mb-1">Error:</div>
              <div className="text-red-400">{this.state.errorMsg ?? "(no message)"}</div>
              {this.state.errorStack && (
                <details className="mt-3">
                  <summary className="cursor-pointer text-muted-foreground hover:text-foreground">
                    Show stack
                  </summary>
                  <pre className="whitespace-pre-wrap text-[11px] mt-2 text-muted-foreground">
                    {this.state.errorStack.slice(0, 2000)}
                  </pre>
                </details>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              <Button
                onClick={this.handleCopy}
                variant="outline"
                size="sm"
                className="border-amber-500/40"
              >
                <Copy className="h-3.5 w-3.5 mr-1.5" />
                {this.state.copyState === "copied" ? "Copied!" : "Copy error"}
              </Button>
              <Button onClick={this.handleReload} variant="outline" size="sm">
                <RotateCcw className="h-3.5 w-3.5 mr-1.5" />
                Reload page
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }
}
